import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Divider, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Skeleton, Alert, Button, Tooltip,
  LinearProgress,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DownloadIcon from '@mui/icons-material/Download';
import { getApiBase } from '../utils/apiConfig';
import { apiFetch } from '../utils/apiFetch.js';
import { useSearchParams } from 'react-router-dom';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n ?? 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color, dark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: 3, flex: '1 1 180px',
        border: dark ? 'none' : '1px solid',
        borderColor: dark ? undefined : 'grey.200',
        background: dark ? 'linear-gradient(145deg, #0f172a, #1e3a5f)' : '#fff',
        color: dark ? '#fff' : 'text.primary',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {dark && <Box sx={{ position: 'absolute', top: -24, right: -24, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(34,166,90,0.12)' }} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2, flexShrink: 0,
          bgcolor: dark ? 'rgba(255,255,255,0.1)' : (color + '.50'),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: dark ? '#4ade80' : (color + '.main'),
        }}>
          {icon}
        </Box>
        <Typography variant="caption" fontWeight={700} sx={{ color: dark ? 'rgba(255,255,255,0.6)' : 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={800} sx={{ color: dark ? '#4ade80' : (color + '.main') }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: dark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }}>
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

// ─── status chip ──────────────────────────────────────────────────────────────
// Driven by Payment.paymentStatus: pending | escrowed | released_to_mover | refunded_to_customer | failed
function StatusChip({ status }) {
  const map = {
    pending:            { label: 'Awaiting Payment', color: 'default' },
    escrowed:           { label: 'In Escrow',         color: 'warning' },
    released_to_mover:  { label: 'Paid Out',          color: 'success' },
    refunded_to_customer: { label: 'Refunded',        color: 'error'   },
    failed:             { label: 'Failed',            color: 'error'   },
  };
  const { label, color } = map[status] ?? { label: status ?? 'Unknown', color: 'default' };
  return <Chip label={label} color={color} size="small" sx={{ fontWeight: 700, fontSize: 11 }} />;
}
// ─── main page ────────────────────────────────────────────────────────────────
export default function MoverFinancials({ token }) {
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectBusy, setConnectBusy]     = useState(false);

  const authHeaders = () => (token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {});

  const fetchConnectStatus = () => {
    const base = getApiBase();
    fetch(`${base}/stripe-connect/account-status/me`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(setConnectStatus)
      .catch(() => setConnectStatus({ hasAccount: false, needsOnboarding: true }));
  };

  useEffect(() => {
    const base = getApiBase();

    fetch(`${base}/payments/me`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then((data) => setPayments(Array.isArray(data) ? data : []))
      .catch(() => setError('Unable to load financial data. Please try again.'))
      .finally(() => setLoading(false));

    fetchConnectStatus();
  }, [token, searchParams]);

  const startStripeOnboarding = async () => {
    setConnectBusy(true);
    try {
      const base = getApiBase();
      const endpoint = connectStatus?.hasAccount ? 'create-onboarding-link' : 'create-connect-account';
      const res = await fetch(`${base}/stripe-connect/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ businessInfo: {} }),
      });
      const data = await res.json();
      if (data.onboardingUrl) window.location.href = data.onboardingUrl;
    } catch {
      setError('Unable to start payout setup. Please try again.');
    } finally {
      setConnectBusy(false);
    }
  };

  const refreshPayoutStatus = () => {
    setConnectBusy(true);
    fetchConnectStatus();
    window.setTimeout(() => setConnectBusy(false), 500);
  };

  // ── computed metrics (grounded in real Payment records, not guessed splits) ──
  const releasedPayments = payments.filter(p => p.paymentStatus === 'released_to_mover');
  const escrowedPayments  = payments.filter(p => p.paymentStatus === 'escrowed');
  const pendingPayments   = payments.filter(p => p.paymentStatus === 'pending');

  const totalEarned   = releasedPayments.reduce((s, p) => s + (p.moverAmount ?? 0), 0);
  const inEscrow       = escrowedPayments.reduce((s, p) => s + (p.moverAmount ?? 0), 0);
  const pendingPayment = pendingPayments.reduce((s, p) => s + (p.totalAmount ?? 0), 0);
  const totalCommission = releasedPayments.reduce((s, p) => s + (p.platformCommission ?? 0), 0);

  const mostRecentRelease = releasedPayments
    .slice()
    .sort((a, b) => new Date(b.escrowReleaseDate ?? b.updatedAt) - new Date(a.escrowReleaseDate ?? a.updatedAt))[0];

  const ledger = payments
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const payoutDetailsSubmitted = Boolean(connectStatus?.hasAccount && connectStatus?.detailsSubmitted);

  // ── loading skeleton ────────────────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={220} height={40} sx={{ mb: 3 }} />
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={110} sx={{ flex: 1 }} />)}
      </Stack>
      <Skeleton variant="rounded" height={300} />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Earnings & Escrow</Typography>
          <Typography variant="body2" color="text.secondary">
            MOVD holds full payment in escrow until the move is completed with no issues, then releases your payout.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ borderRadius: 99, fontWeight: 600 }}
          onClick={async () => {
            try {
              const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
              const res = await apiFetch('/api/analytics/export/bookings', { headers });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `movd-statement-${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch { /* silent fail — download is non-critical */ }
          }}
        >
          Export Statement
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── KPI row ─────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
        <KpiCard
          dark
          icon={<AccountBalanceWalletIcon />}
          label="Total Earned"
          value={fmt(totalEarned)}
          sub={`${releasedPayments.length} completed & paid out`}
          color="success"
        />
        <KpiCard
          icon={<LockIcon />}
          label="In Escrow"
          value={fmt(inEscrow)}
          sub={`${escrowedPayments.length} job${escrowedPayments.length !== 1 ? 's' : ''} awaiting release`}
          color="warning"
        />
        <KpiCard
          icon={<HourglassTopIcon />}
          label="Awaiting Payment"
          value={fmt(pendingPayment)}
          sub={`${pendingPayments.length} booking${pendingPayments.length !== 1 ? 's' : ''} not yet paid`}
          color="default"
        />
        <KpiCard
          icon={<CheckCircleIcon />}
          label="Last Payout"
          value={mostRecentRelease ? fmt(mostRecentRelease.moverAmount) : 'None yet'}
          sub={mostRecentRelease ? fmtDate(mostRecentRelease.escrowReleaseDate) : 'Complete a job to earn'}
          color="info"
        />
      </Stack>

      {/* ── Two-column body ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexDirection: { xs: 'column', lg: 'row' } }}>

        {/* LEFT: Job Ledger */}
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'grey.200', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'grey.200', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle1" fontWeight={700}>Job Ledger</Typography>
              <Tooltip title="Your payout is the job total minus MOVD's commission, released once the move is confirmed complete with no dispute.">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5, cursor: 'help' }} />
              </Tooltip>
            </Box>

            {ledger.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <LocalShippingIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                <Typography color="text.secondary">No payments yet</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {['Job / Customer','Move Date','Job Total','Commission','Your Payout','Status'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.slice(0, 30).map((p) => (
                      <TableRow key={p._id} hover sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {p.jobNumber || p.booking?._id?.slice(-6)?.toUpperCase() || p._id?.slice(-6)?.toUpperCase()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.customerName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fmtDate(p.moveDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fmt(p.totalAmount)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">-{fmt(p.platformCommission)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} color="success.main">{fmt(p.moverAmount)}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={p.paymentStatus} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>

        {/* RIGHT: How it works + breakdown */}
        <Box sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* Payout account (Stripe Connect) */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: connectStatus?.isOnboardingComplete ? 'success.200' : 'warning.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18, color: connectStatus?.isOnboardingComplete ? 'success.main' : 'warning.main' }} />
              <Typography variant="subtitle2" fontWeight={700}>Payout Account</Typography>
            </Box>
            {connectStatus?.isOnboardingComplete ? (
              <>
                <Typography variant="body2" color="success.main" fontWeight={600} sx={{ mb: 1 }}>
                  Connected — payouts can be sent to your bank
                </Typography>
                <Button size="small" variant="outlined" disabled={connectBusy} onClick={startStripeOnboarding}>
                  Update payout details
                </Button>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {payoutDetailsSubmitted
                    ? 'Your Stripe details were submitted. Payout activation is pending; we will show Connected as soon as Stripe enables payouts.'
                    : connectStatus?.hasAccount
                      ? "You're part way through payout setup — finish it so MOVD can pay you out."
                      : 'Connect a bank account so MOVD can pay you once a job is confirmed complete.'}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  disabled={connectBusy}
                  onClick={payoutDetailsSubmitted ? refreshPayoutStatus : startStripeOnboarding}
                >
                  {payoutDetailsSubmitted ? 'Refresh payout status' : connectStatus?.hasAccount ? 'Finish payout setup' : 'Set up payouts'}
                </Button>
              </>
            )}
          </Paper>

          {/* How escrow works */}
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'grey.200', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>How Escrow Works</Typography>
            <Stack spacing={1.5}>
              {[
                { icon: '💳', text: 'Customer pays the full job total up front' },
                { icon: '🔒', text: 'MOVD holds funds in escrow while the move is in progress' },
                { icon: '✅', text: 'Once the customer confirms completion with no dispute, MOVD releases your payout' },
                { icon: '💼', text: "MOVD's commission is deducted before your payout" },
                { icon: '🛟', text: "If the customer doesn't confirm, funds still auto-release after the hold window unless disputed" },
              ].map(({ icon, text }, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Typography sx={{ fontSize: 16, mt: 0.1 }}>{icon}</Typography>
                  <Typography variant="body2" color="text.secondary">{text}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Earnings progress bar */}
          {(totalEarned + inEscrow + totalCommission) > 0 && (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={0.5}>Earnings Breakdown</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                across all payments on file
              </Typography>
              <Stack spacing={1.5}>
                {(() => {
                  const max = Math.max(totalEarned, inEscrow, totalCommission, 1);
                  return [
                    { label: 'Paid out to you', value: totalEarned, color: '#22a65a' },
                    { label: 'In escrow', value: inEscrow, color: '#f59e0b' },
                    { label: "MOVD's commission", value: totalCommission, color: '#94a3b8' },
                  ].map(({ label, value, color }) => (
                    <Box key={label}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={600}>{label}</Typography>
                        <Typography variant="caption" fontWeight={700}>{fmt(value)}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(value / max) * 100}
                        sx={{
                          height: 6, borderRadius: 99,
                          bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 99 },
                        }}
                      />
                    </Box>
                  ));
                })()}
              </Stack>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}
